import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { AdminSettings as Settings, AppSettingsPatch, Plan, SectionKey } from "../../types";
import {
  fetchAdminSettings, updateAdminSettings,
  adminCreatePlan, adminUpdatePlan, adminDeletePlan,
} from "../../utils/api";
import { AppHeader, Banner, SectionHead, Icon, Field } from "../common/ui";
import { useAppConfig } from "../../config/AppConfigContext";
import PaymentsPanel from "./PaymentsPanel";

const SECTION_KEYS: SectionKey[] = ["selection", "study", "history", "games", "chat"];

/** A labelled on/off switch. */
function Toggle({
  label, hint, checked, onChange,
}: {
  label: string; hint?: string; checked: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <button
      onClick={() => onChange(!checked)}
      role="switch"
      aria-checked={checked}
      className="glass glass-1"
      style={{
        width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "12px 14px",
        borderRadius: 14, border: "none", cursor: "pointer", color: "inherit", textAlign: "left",
      }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>
        <span className="t-sm" style={{ fontWeight: 700, display: "block" }}>{label}</span>
        {hint && <span className="t-xs muted-2" style={{ display: "block", marginTop: 2 }}>{hint}</span>}
      </span>
      <span style={{
        flex: "none", width: 44, height: 26, borderRadius: 999, position: "relative",
        background: checked ? "var(--accent-fill)" : "var(--glass-3)",
        boxShadow: "inset 0 0 0 1px var(--border)", transition: "background 0.18s var(--ease)",
      }}>
        <span style={{
          position: "absolute", top: 3, left: checked ? 21 : 3, width: 20, height: 20,
          borderRadius: "50%", background: checked ? "var(--accent-ink)" : "var(--text-2)",
          transition: "left 0.18s var(--ease)",
        }} />
      </span>
    </button>
  );
}

/** Small labelled number input. */
function NumField({
  label, hint, value, onChange, min = 0,
}: {
  label: string; hint?: string; value: number; onChange: (v: number) => void; min?: number;
}) {
  return (
    <label style={{ display: "block" }}>
      <span className="t-sm" style={{ fontWeight: 700 }}>{label}</span>
      {hint && <span className="t-xs muted-2" style={{ display: "block", marginTop: 2 }}>{hint}</span>}
      <input
        type="number"
        min={min}
        value={value}
        onChange={(e) => onChange(Math.max(min, parseInt(e.target.value, 10) || 0))}
        className="glass glass-1"
        style={{
          width: "100%", marginTop: 7, padding: "11px 13px", borderRadius: 12,
          border: "1px solid var(--border)", background: "var(--glass-1)", color: "var(--text-1)",
          font: "inherit", fontWeight: 700,
        }}
      />
    </label>
  );
}

/** One row of the plan editor. */
function PlanRow({
  plan, currency, currencyEn, blocks, onChange, onDelete,
}: {
  plan: Plan; currency: string; currencyEn: string;
  blocks: { key: string; title_ru: string; title_en: string }[];
  onChange: (patch: Partial<Plan>) => void;
  onDelete: () => void;
}) {
  const { t, i18n } = useTranslation();
  const isRu = !i18n.language?.startsWith("en");
  const grants = plan.grants ?? [];
  const daysFor = (key: string) => grants.find((g) => g.block === key)?.days ?? 0;
  // Keep the canonical block order; 0 days = the block is not part of the plan.
  const setDaysFor = (key: string, days: number) => onChange({
    grants: blocks
      .map((b) => ({ block: b.key, days: b.key === key ? days : daysFor(b.key) }))
      .filter((g) => g.days > 0),
  });
  const input: React.CSSProperties = {
    padding: "9px 11px", borderRadius: 10, border: "1px solid var(--border)",
    background: "var(--glass-1)", color: "var(--text-1)", font: "inherit", width: "100%",
  };

  return (
    <div className="glass glass-1" style={{ padding: 14, borderRadius: 16 }}>
      <div className="row between" style={{ gap: 8, marginBottom: 10 }}>
        <input
          value={plan.title_ru}
          onChange={(e) => onChange({ title_ru: e.target.value })}
          placeholder={t("adminSettings.planTitleRu")}
          style={{ ...input, fontWeight: 800 }}
        />
        <button
          className="icon-btn"
          onClick={onDelete}
          aria-label={t("adminSettings.planDelete")}
          style={{ flex: "none", color: "var(--danger, #c05252)" }}
        >
          <Icon name="trash" size={17} />
        </button>
      </div>

      <input
        value={plan.title_en}
        onChange={(e) => onChange({ title_en: e.target.value })}
        placeholder={t("adminSettings.planTitleEn")}
        style={{ ...input, marginBottom: 10 }}
      />

      <label style={{ display: "block", marginBottom: 10 }}>
        <span className="t-xs muted-2">{t("adminSettings.planTier")}</span>
        <select
          value={plan.tier ?? ""}
          onChange={(e) => onChange({ tier: (e.target.value || null) as Plan["tier"] })}
          style={{ ...input, marginTop: 4 }}
        >
          <option value="">{t("adminSettings.planTierNone")}</option>
          <option value="mixes">MIXES</option>
          <option value="challenge">CHALLENGE + MIXES</option>
          <option value="feedback">CHALLENGE + MIXES + {t("adminSettings.planTierFeedback")}</option>
        </select>
      </label>

      <label style={{ display: "block", marginBottom: 10 }}>
        <span className="t-xs muted-2">{t("adminSettings.planPayLink")}</span>
        <input
          type="url" inputMode="url"
          value={plan.pay_link ?? ""}
          onChange={(e) => onChange({ pay_link: e.target.value.trim() || null })}
          placeholder="https://api.bepaid.by/products/…/pay"
          style={{ ...input, marginTop: 4 }}
        />
      </label>

      <div className="row" style={{ gap: 8, marginBottom: 10 }}>
        <label style={{ flex: 1, minWidth: 0 }}>
          <span className="t-xs muted-2">{t("adminSettings.planPrice")} ({currency})</span>
          <input
            type="number" min={0} step="0.01"
            value={String(plan.price ?? 0)}
            onChange={(e) => onChange({ price: e.target.value })}
            style={{ ...input, marginTop: 4, fontWeight: 800 }}
          />
        </label>
        <label style={{ flex: 1, minWidth: 0 }}>
          <span className="t-xs muted-2">{t("adminSettings.planPriceUsd")} ({currencyEn})</span>
          <input
            type="number" min={0} step="0.01"
            value={String(plan.price_usd ?? 0)}
            onChange={(e) => onChange({ price_usd: e.target.value })}
            style={{ ...input, marginTop: 4, fontWeight: 800 }}
          />
        </label>
        <label style={{ flex: 1, minWidth: 0 }}>
          <span className="t-xs muted-2">{t("adminSettings.planDays")}</span>
          <input
            type="number" min={1}
            value={plan.days}
            onChange={(e) => onChange({ days: Math.max(1, parseInt(e.target.value, 10) || 1) })}
            style={{ ...input, marginTop: 4, fontWeight: 800 }}
          />
        </label>
      </div>

      <div className="row" style={{ gap: 8, marginBottom: 10 }}>
        <input
          value={plan.badge_ru ?? ""}
          onChange={(e) => onChange({ badge_ru: e.target.value || null })}
          placeholder={t("adminSettings.planBadgeRu")}
          style={input}
        />
        <input
          value={plan.badge_en ?? ""}
          onChange={(e) => onChange({ badge_en: e.target.value || null })}
          placeholder={t("adminSettings.planBadgeEn")}
          style={input}
        />
      </div>

      <div className="plan-grants">
        <span className="t-xs muted-2" style={{ fontWeight: 700 }}>{t("adminSettings.planGrants")}</span>
        {blocks.map((b) => (
          <label key={b.key} className="plan-grants__row">
            <span className="t-sm">{isRu ? b.title_ru : b.title_en}</span>
            <input
              type="number" min={0} max={3650} inputMode="numeric"
              value={daysFor(b.key) || ""}
              placeholder="—"
              onChange={(e) => setDaysFor(b.key, Math.min(3650, Math.max(0, parseInt(e.target.value, 10) || 0)))}
              aria-label={`${isRu ? b.title_ru : b.title_en}: ${t("adminSettings.planDays")}`}
              style={{ ...input, width: 84, textAlign: "right", fontWeight: 800 }}
            />
          </label>
        ))}
        <span className="t-xs muted-2" style={{ lineHeight: 1.4 }}>
          {grants.length ? t("adminSettings.planGrantsHint") : t("adminSettings.planGrantsEmpty")}
        </span>
      </div>

      <Toggle
        label={t("adminSettings.planActive")}
        hint={Number(plan.price) === 0 ? t("adminSettings.planZeroPrice") : undefined}
        checked={plan.is_active}
        onChange={(v) => onChange({ is_active: v })}
      />
    </div>
  );
}

/** A decimal typed freely ("3,6" on the way to "3,66"); only valid values reach the settings. */
function RateField({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  const [text, setText] = useState(String(value));
  return (
    <Field
      icon="wallet"
      name="byn_per_100_rub"
      value={text}
      onChange={(v) => {
        setText(v);
        const n = Number(v.replace(",", "."));
        if (n > 0 && n <= 1000) onChange(n);
      }}
      placeholder="3.66"
    />
  );
}

export default function AdminSettings({
  token, onExit, theme, onThemeChange,
}: {
  token: string;
  onExit: () => void;
  theme: "dark" | "light";
  onThemeChange: (t: "dark" | "light") => void;
}) {
  const { t } = useTranslation();
  const { reload, config } = useAppConfig();

  const [s, setS] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: "success" | "error"; text: string } | null>(null);

  useEffect(() => {
    fetchAdminSettings(token)
      .then(setS)
      .catch((e) => setMsg({ kind: "error", text: e instanceof Error ? e.message : "" }))
      .finally(() => setLoading(false));
  }, [token]);

  const patch = (p: Partial<Settings>) => setS((prev) => (prev ? { ...prev, ...p } : prev));

  /** Persist everything in one go: the settings blob, then each plan. */
  const save = async () => {
    if (!s) return;
    setBusy(true);
    setMsg(null);
    try {
      const body: AppSettingsPatch = {
        sections: s.sections,
        courses: s.courses,
        free_selection_limit: s.free_selection_limit,
        free_lessons_per_section: s.free_lessons_per_section,
        maintenance_mode: s.maintenance_mode,
        maintenance_message_ru: s.maintenance_message_ru,
        maintenance_message_en: s.maintenance_message_en,
        contact_handle: s.contact_handle,
        telegram_browser_checkout: s.telegram_browser_checkout ?? true,
        upgrade_credit_to: s.upgrade_credit_to ?? ["challenge"],
        announcement: s.announcement,
        pricing_notice: s.pricing_notice,
        currency: s.currency,
        currency_en: s.currency_en,
        byn_per_100_rub: s.byn_per_100_rub ?? 3.66,
      };
      await updateAdminSettings(body, token);
      for (const p of s.plans) {
        await adminUpdatePlan(p.id, {
          code: p.code, title_ru: p.title_ru, title_en: p.title_en,
          badge_ru: p.badge_ru, badge_en: p.badge_en,
          days: p.days, price: p.price, price_usd: p.price_usd,
          sort_order: p.sort_order, is_active: p.is_active,
          grants: p.grants ?? [],
          tier: p.tier ?? null,
          pay_link: p.pay_link ?? null,
        }, token);
      }
      const fresh = await fetchAdminSettings(token);
      setS(fresh);
      reload();                       // the admin's own app picks the change up now
      setMsg({ kind: "success", text: t("adminSettings.saved") });
    } catch (e) {
      setMsg({ kind: "error", text: e instanceof Error ? e.message : t("common.error") });
    } finally {
      setBusy(false);
    }
  };

  const addPlan = async () => {
    if (!s) return;
    setBusy(true);
    try {
      const created = await adminCreatePlan({
        code: `plan-${Date.now()}`,
        title_ru: t("adminSettings.newPlan"),
        title_en: "New plan",
        badge_ru: null, badge_en: null,
        days: 30, price: 0, price_usd: 0,
        sort_order: s.plans.length + 1,
        is_active: false,          // start hidden until it is filled in and priced
      }, token);
      patch({ plans: [...s.plans, created] });
    } catch (e) {
      setMsg({ kind: "error", text: e instanceof Error ? e.message : t("common.error") });
    } finally {
      setBusy(false);
    }
  };

  const removePlan = async (id: string) => {
    if (!s) return;
    setBusy(true);
    try {
      await adminDeletePlan(id, token);
      patch({ plans: s.plans.filter((p) => p.id !== id) });
      reload();
    } catch (e) {
      setMsg({ kind: "error", text: e instanceof Error ? e.message : t("common.error") });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="beeapp__scroll">
      <AppHeader theme={theme} onThemeChange={onThemeChange} onBack={onExit} title={t("adminSettings.title")} />
      <div className="wrap content-cap" style={{ paddingTop: 6 }}>
        {loading && <div className="spinner spinner-amber" style={{ margin: "40px auto" }} />}

        {s && (
          <div className="stack" style={{ "--gap": "18px" } as React.CSSProperties}>
            {msg && <Banner kind={msg.kind === "success" ? "info" : "error"}>{msg.text}</Banner>}

            {/* ── Sections ── */}
            <section className="glass glass-lg" style={{ padding: 20 }}>
              <SectionHead icon="toggle">{t("adminSettings.sections")}</SectionHead>
              <p className="t-xs muted-2" style={{ margin: "0 0 12px", lineHeight: 1.45 }}>
                {t("adminSettings.sectionsHint")}
              </p>
              <div className="stack" style={{ "--gap": "9px" } as React.CSSProperties}>
                {SECTION_KEYS.map((k) => (
                  <Toggle
                    key={k}
                    label={t(`adminSettings.section_${k}`)}
                    checked={s.sections[k]}
                    onChange={(v) => patch({ sections: { ...s.sections, [k]: v } })}
                  />
                ))}
              </div>
            </section>

            {/* ── Plans & prices ── */}
            <section className="glass glass-lg" style={{ padding: 20 }}>
              <SectionHead icon="wallet">{t("adminSettings.plans")}</SectionHead>
              <p className="t-xs muted-2" style={{ margin: "0 0 12px", lineHeight: 1.45 }}>
                {t("adminSettings.plansHint")}
              </p>

              {/* Two storefronts, two labels: ₽ for the Russian UI, $ for the
                  English one. Prices are per-storefront too (fields below). */}
              <div className="row" style={{ gap: 10, marginBottom: 14, maxWidth: 460 }}>
                <div style={{ flex: 1 }}>
                  <p className="t-sm" style={{ fontWeight: 700, marginBottom: 7 }}>{t("adminSettings.currency")}</p>
                  <Field
                    icon="wallet"
                    name="currency"
                    value={s.currency}
                    onChange={(v) => patch({ currency: v.slice(0, 8) })}
                    placeholder="₽"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <p className="t-sm" style={{ fontWeight: 700, marginBottom: 7 }}>{t("adminSettings.currencyEn")}</p>
                  <Field
                    icon="wallet"
                    name="currency_en"
                    value={s.currency_en}
                    onChange={(v) => patch({ currency_en: v.slice(0, 8) })}
                    placeholder="$"
                  />
                </div>
              </div>
              {/* bePaid charges BYN: the ₽ price at this rate, whole roubles. */}
              <div style={{ marginBottom: 14, maxWidth: 460 }}>
                <p className="t-sm" style={{ fontWeight: 700, marginBottom: 7 }}>{t("adminSettings.bynRate")}</p>
                <RateField value={s.byn_per_100_rub ?? 3.66} onChange={(n) => patch({ byn_per_100_rub: n })} />
                <p className="t-xs muted-2" style={{ margin: "6px 0 0", lineHeight: 1.4 }}>{t("adminSettings.bynRateHint")}</p>
              </div>

              <div className="stack" style={{ "--gap": "12px" } as React.CSSProperties}>
                {s.plans.map((p) => (
                  <PlanRow
                    key={p.id}
                    plan={p}
                    currency={s.currency}
                    currencyEn={s.currency_en}
                    blocks={config.blocks ?? []}
                    onChange={(pp) => patch({ plans: s.plans.map((x) => (x.id === p.id ? { ...x, ...pp } : x)) })}
                    onDelete={() => removePlan(p.id)}
                  />
                ))}
              </div>

              <button className="btn btn-glass btn-block" onClick={addPlan} disabled={busy} style={{ marginTop: 12 }}>
                <Icon name="plus" size={17} /> {t("adminSettings.addPlan")}
              </button>
            </section>

            {/* ── Online payments ── */}
            <PaymentsPanel token={token} />
            <section className="glass glass-lg" style={{ padding: 20 }}>
              <SectionHead icon="sparkles">{t("adminSettings.upgradeCredit")}</SectionHead>
              <p className="t-xs muted-2" style={{ margin: "0 0 12px", lineHeight: 1.45 }}>{t("adminSettings.upgradeCreditHint")}</p>
              <div className="stack" style={{ "--gap": "9px" } as React.CSSProperties}>
                {(["challenge", "feedback"] as const).map((tier) => {
                  const list = s.upgrade_credit_to ?? ["challenge"];
                  return (
                    <Toggle
                      key={tier}
                      label={t(`adminSettings.upgradeCredit_${tier}`)}
                      checked={list.includes(tier)}
                      onChange={(v) => patch({ upgrade_credit_to: v ? [...list.filter((x) => x !== tier), tier] : list.filter((x) => x !== tier) })}
                    />
                  );
                })}
              </div>
            </section>
            <Toggle
              label={t("adminSettings.browserCheckout")}
              hint={t("adminSettings.browserCheckoutHint")}
              checked={s.telegram_browser_checkout ?? true}
              onChange={(v) => patch({ telegram_browser_checkout: v })}
            />

            {/* ── Courses ── */}
            <section className="glass glass-lg" style={{ padding: 20 }}>
              <SectionHead icon="graduation">{t("adminSettings.courses")}</SectionHead>
              <p className="t-xs muted-2" style={{ marginBottom: 12, lineHeight: 1.45 }}>
                {t("adminSettings.coursesHint")}
              </p>
              <div className="stack" style={{ "--gap": "11px" } as React.CSSProperties}>
                {(["base", "ombre"] as const).map((key) => (
                  <Toggle
                    key={key}
                    label={t(`study.course_${key}`)}
                    hint={t(`adminSettings.course_${key}_hint`)}
                    checked={!!s.courses?.[key]}
                    onChange={(v) => patch({ courses: { ...s.courses, [key]: v } })}
                  />
                ))}
              </div>
            </section>

            {/* ── Free limits ── */}
            <section className="glass glass-lg" style={{ padding: 20 }}>
              <SectionHead icon="sliders">{t("adminSettings.limits")}</SectionHead>
              <div className="stack" style={{ "--gap": "14px" } as React.CSSProperties}>
                <NumField
                  label={t("adminSettings.freeSelections")}
                  hint={t("adminSettings.freeSelectionsHint")}
                  value={s.free_selection_limit}
                  onChange={(v) => patch({ free_selection_limit: v })}
                />
                <NumField
                  label={t("adminSettings.freeLessons")}
                  hint={t("adminSettings.freeLessonsHint")}
                  value={s.free_lessons_per_section}
                  onChange={(v) => patch({ free_lessons_per_section: v })}
                />
              </div>
            </section>

            {/* ── Announcement ── */}
            <section className="glass glass-lg" style={{ padding: 20 }}>
              <SectionHead icon="sparkles">{t("adminSettings.announcement")}</SectionHead>
              <div className="stack" style={{ "--gap": "11px" } as React.CSSProperties}>
                <Toggle
                  label={t("adminSettings.announcementOn")}
                  hint={t("adminSettings.announcementHint")}
                  checked={s.announcement.enabled}
                  onChange={(v) => patch({ announcement: { ...s.announcement, enabled: v } })}
                />
                <Field
                  icon="message" name="ann_ru"
                  value={s.announcement.text_ru}
                  onChange={(v) => patch({ announcement: { ...s.announcement, text_ru: v } })}
                  placeholder={t("adminSettings.announcementRu")}
                />
                <Field
                  icon="message" name="ann_en"
                  value={s.announcement.text_en}
                  onChange={(v) => patch({ announcement: { ...s.announcement, text_en: v } })}
                  placeholder={t("adminSettings.announcementEn")}
                />
              </div>
            </section>

            {/* ── Launch-price notice above the tariffs ── */}
            {s.pricing_notice && (
            <section className="glass glass-lg" style={{ padding: 20 }}>
              <SectionHead icon="wallet">{t("adminSettings.pricingNotice")}</SectionHead>
              <div className="stack" style={{ "--gap": "11px" } as React.CSSProperties}>
                <Toggle
                  label={t("adminSettings.pricingNoticeOn")}
                  hint={t("adminSettings.pricingNoticeHint")}
                  checked={s.pricing_notice.enabled}
                  onChange={(v) => patch({ pricing_notice: { ...s.pricing_notice!, enabled: v } })}
                />
                <textarea className="textarea" rows={3} value={s.pricing_notice.text_ru}
                  onChange={(e) => patch({ pricing_notice: { ...s.pricing_notice!, text_ru: e.target.value } })}
                  placeholder={t("adminSettings.announcementRu")} aria-label={t("adminSettings.announcementRu")} />
                <textarea className="textarea" rows={3} value={s.pricing_notice.text_en}
                  onChange={(e) => patch({ pricing_notice: { ...s.pricing_notice!, text_en: e.target.value } })}
                  placeholder={t("adminSettings.announcementEn")} aria-label={t("adminSettings.announcementEn")} />
              </div>
            </section>
            )}

            {/* ── Contact & maintenance ── */}
            <section className="glass glass-lg" style={{ padding: 20 }}>
              <SectionHead icon="wrench">{t("adminSettings.service")}</SectionHead>
              <div className="stack" style={{ "--gap": "11px" } as React.CSSProperties}>
                <div>
                  <p className="t-sm" style={{ fontWeight: 700, marginBottom: 7 }}>{t("adminSettings.contact")}</p>
                  <Field
                    icon="message" name="contact"
                    value={s.contact_handle}
                    onChange={(v) => patch({ contact_handle: v })}
                    placeholder="@username"
                  />
                  <p className="t-xs muted-2" style={{ marginTop: 6 }}>{t("adminSettings.contactHint")}</p>
                </div>

                <Toggle
                  label={t("adminSettings.maintenance")}
                  hint={t("adminSettings.maintenanceHint")}
                  checked={s.maintenance_mode}
                  onChange={(v) => patch({ maintenance_mode: v })}
                />
                {s.maintenance_mode && (
                  <Banner kind="error">{t("adminSettings.maintenanceWarning")}</Banner>
                )}
                <Field
                  icon="message" name="maint_ru"
                  value={s.maintenance_message_ru}
                  onChange={(v) => patch({ maintenance_message_ru: v })}
                  placeholder={t("adminSettings.maintenanceRu")}
                />
                <Field
                  icon="message" name="maint_en"
                  value={s.maintenance_message_en}
                  onChange={(v) => patch({ maintenance_message_en: v })}
                  placeholder={t("adminSettings.maintenanceEn")}
                />
              </div>
            </section>

            <button className="btn btn-primary btn-block" onClick={save} disabled={busy} style={{ marginBottom: 8 }}>
              {busy ? <span className="spinner" /> : <><Icon name="save" size={18} /> {t("adminSettings.save")}</>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
